import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];

/**
 * SingleImageField — a drop-in replacement for a "paste image URL" text
 * input. Uploads to S3 via /api/uploads and stores the returned key in
 * the parent via onChange(key). Renders the current value (key, legacy
 * path, or full URL — `imgUrl()` normalises all three).
 *
 * Props:
 *   label    — visible label above the control
 *   value    — current image key/url (string | null)
 *   onChange — (key:string|null) => void
 *   hint     — optional helper text
 *   aspect   — tailwind aspect ratio class (default "aspect-[4/3]")
 *   testid
 */
export default function SingleImageField({
  label,
  value,
  onChange,
  hint,
  aspect = "aspect-[4/3]",
  testid = "single-image-field",
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Only JPG/PNG/WebP/GIF/HEIC allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image exceeds 10MB");
      return;
    }
    upload(file);
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.key || data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const clear = () => onChange(null);

  return (
    <div data-testid={testid}>
      {label && <label className="overline block mb-1">{label}</label>}
      <div
        className={`relative ${aspect} border border-dashed border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden flex items-center justify-center`}
      >
        {value ? (
          <>
            <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1.5 shadow rounded-sm"
              data-testid={`${testid}-clear`}
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={() => ref.current?.click()}
              className="absolute bottom-2 right-2 bg-black/70 hover:bg-black text-white text-xs px-3 py-1.5 rounded-sm"
              data-testid={`${testid}-replace`}
              disabled={busy}
            >
              {busy ? "Uploading…" : "Replace"}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            className="flex flex-col items-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
            data-testid={`${testid}-upload`}
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
            {busy ? "Uploading…" : "Upload image"}
          </button>
        )}
      </div>
      {hint && <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">{hint}</p>}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        onChange={pick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
